import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaBars, FaSignOutAlt, FaUser, FaSun, FaMoon, FaDesktop } from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';

const THEMES = [
  { key: 'light', label: 'Claro', icon: FaSun },
  { key: 'dark', label: 'Oscuro', icon: FaMoon },
  { key: 'system', label: 'Como el sistema', icon: FaDesktop },
];

/** Tema: solo en pantallas ya rediseñadas (las demás siguen en oscuro). */
function ThemeSwitch() {
  const { pref, setPref, themed } = useTheme();
  if (!themed) return null;
  return (
    <div role="radiogroup" aria-label="Tema" className="hidden sm:flex items-center gap-0.5 rounded-full bg-raised p-0.5 ring-1 ring-line">
      {THEMES.map(({ key, label, icon: Icon }) => (
        <button key={key} type="button" role="radio" aria-checked={pref === key} title={label} aria-label={label} onClick={() => setPref(key)}
          className={`h-7 w-7 grid place-items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/70
            ${pref === key ? 'bg-panel text-fg shadow-sm' : 'text-fg-faint hover:text-fg'}`}>
          <Icon aria-hidden className="h-3 w-3" />
        </button>
      ))}
    </div>
  );
}

function UserMenu() {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const out = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') { setOpen(false); ref.current?.querySelector('button')?.focus(); } };
    document.addEventListener('mousedown', out); document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', out); document.removeEventListener('keydown', esc); };
  }, [open]);
  const name = currentUser?.full_name || 'Admin';
  const item = 'w-full flex items-center gap-2.5 px-3 h-9 rounded-lg text-[13.5px] text-fg-dim hover:text-fg hover:bg-raised focus-visible:outline-none focus-visible:bg-raised focus-visible:text-fg';
  return (
    <div className="relative" ref={ref}>
      <button type="button" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2.5 h-10 pl-3 pr-1 rounded-full hover:bg-raised transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/70">
        <span className="text-[13.5px] text-fg-dim hidden md:block max-w-[160px] truncate">{name}</span>
        <span className="h-8 w-8 rounded-full grid place-items-center text-[13px] font-bold text-white" style={{ background: 'linear-gradient(135deg, #DC1F26, #FF6B3D)' }} aria-hidden>
          {name.charAt(0).toUpperCase()}
        </span>
      </button>
      {open && (
        <div role="menu" className="absolute right-0 top-full mt-2 w-52 rounded-2xl bg-panel ring-1 ring-line p-1.5 z-50 animate-scale-in origin-top-right" style={{ boxShadow: 'var(--shadow)' }}>
          <button role="menuitem" type="button" className={item} onClick={() => { setOpen(false); navigate('/profile'); }}><FaUser aria-hidden className="h-3 w-3" />Mi perfil</button>
          <button role="menuitem" type="button" className={item} onClick={logout}><FaSignOutAlt aria-hidden className="h-3 w-3 text-signal" />Cerrar sesión</button>
        </div>
      )}
    </div>
  );
}

const Header = ({ title = '', menuOpen, onToggleMenu }) => (
  <header className="z-10 flex items-center gap-3 px-4 sm:px-6 h-16 shrink-0 bg-ink/85 backdrop-blur border-b border-line">
    <button type="button" onClick={onToggleMenu} aria-label={menuOpen ? 'Ocultar el menú' : 'Mostrar el menú'} aria-expanded={menuOpen}
      className="h-9 w-9 grid place-items-center rounded-full text-fg-dim hover:text-fg hover:bg-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-live/70">
      <FaBars aria-hidden className="h-3.5 w-3.5" />
    </button>
    <p className="flex-1 min-w-0 truncate font-display text-[17px] font-semibold text-fg">{title}</p>
    <ThemeSwitch />
    <UserMenu />
  </header>
);

export default Header;
